import { useEffect, useId, useState, type ReactNode } from 'react';
import {
  can,
  canHoldVisits,
  type CommercialJobCreateInput,
  type JobCreateInput,
  type Role,
} from '@servwell/shared';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { Sheet } from '../ui/Sheet';
import { Notice } from '../ui/Badge';
import { CustomerPicker } from '../ui/CustomerPicker';
import {
  ServiceTypePicker,
  offeredServiceTypes,
  pickedFrom,
  refetchServiceTypes,
} from '../servicetypes/ServiceTypePicker';
import { CreateFooter } from './CreateFooter';
import { Field, field } from './Field';
import { TemplatePicker } from './TemplatePicker';
import type { JobPipeline } from '../jobs/JobPipelineBoard';
import { useOnline } from '../ui/useConnection';
import { useSession } from '../session';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconJobs } from '../icons';
import {
  CommercialWriteRecovery,
  useCommercialWriteRecovery,
} from '../jobs/CommercialWriteRecovery';

/** What a job needs before it can be created: the fields the server requires. */
type NewJobDraft = Pick<
  JobCreateInput,
  'customerId' | 'propertyId' | 'title' | 'serviceTypeId'
>;

/**
 * What the sheet still needs before it can create the job, in the order the form asks for it.
 * Empty means ready.
 *
 * The service type is one of them (ops insights ticket 03): every new job is classified from
 * the start, so a job without one does not save. The server refuses it too; this is the sheet
 * saying so before anyone presses anything.
 *
 * `offered` is what the picker offers (`offeredServiceTypes`), empty until the list has
 * loaded. A type id that is not among them counts as missing at once: one turned off or deleted
 * since it was picked (a refused create refetches the list, and so does a return to the tab)
 * would only be refused again, so Create goes off and says a type is needed.
 */
export function newJobMissing(
  draft: NewJobDraft,
  offered: readonly { id: string }[],
): string[] {
  const missing: string[] = [];
  if (!draft.customerId || !draft.propertyId) missing.push('a customer and address');
  if (!draft.title.trim()) missing.push('what needs doing');
  if (!pickedFrom(offered, draft.serviceTypeId)) missing.push('a service type');
  return missing;
}

/**
 * Book work directly — the "just go fix it" path that never had a quote. Scheduling is
 * optional here: plenty of work is taken today and slotted tomorrow, and forcing a date
 * would push people to invent one.
 */
export function NewJobSheet({
  open,
  onClose,
  commercialPipeline,
}: {
  open: boolean;
  onClose: () => void;
  commercialPipeline?: JobPipeline;
}) {
  const trpc = useTRPC();
  const mayReadPipeline = can(useSession().role, 'jobPipeline', 'read');
  const workflow = useQuery({
    ...trpc.jobPipeline.list.queryOptions(),
    enabled: open && !commercialPipeline && mayReadPipeline,
  });
  if (!open) return null;
  if (commercialPipeline)
    return <CommercialNewJobSheet open onClose={onClose} pipeline={commercialPipeline} />;
  if (!mayReadPipeline) return <LegacyNewJobSheet open onClose={onClose} />;
  const state = shownState(workflow, workflow.data);
  if (state !== 'ready')
    return (
      <Sheet open onClose={onClose} title="New job">
        <UnresolvedState
          state={state}
          icon={IconJobs}
          what="the job workspace"
          error={workflow.error}
          onRetry={() => void workflow.refetch()}
        />
      </Sheet>
    );
  if (workflow.data?.enabled) {
    const pipeline = workflow.data.pipelines.find(
      (candidate) => candidate.id === workflow.data.defaultPipelineId && candidate.active,
    );
    if (!pipeline)
      return (
        <Sheet open onClose={onClose} title="New job">
          <Notice tone="warn">
            Choose an active default pipeline in Settings before creating a Job.
          </Notice>
        </Sheet>
      );
    return (
      <CommercialNewJobSheet
        open
        onClose={onClose}
        pipeline={pipeline}
        workspaceNotice={
          <StaleNotice
            show={refreshFailed(workflow, workflow.data) || workflow.isPaused}
            onRetry={() => void workflow.refetch()}
          />
        }
      />
    );
  }
  return <LegacyNewJobSheet open onClose={onClose} />;
}

function CommercialNewJobSheet({
  open,
  onClose,
  pipeline,
  workspaceNotice,
}: {
  open: boolean;
  onClose: () => void;
  pipeline: JobPipeline;
  workspaceNotice?: ReactNode;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const reasonId = useId();
  const [who, setWho] = useState({ customerId: '', propertyId: '' });
  const [contactName, setContactName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [commercialServiceTypeId, setCommercialServiceTypeId] = useState('');
  const [ownerUserId, setOwner] = useState('');
  const [stageId, setStageId] = useState(
    pipeline.stages.find((stage) => stage.active && stage.outcome === 'open')?.id ?? '',
  );
  const [requestedOn, setRequestedOn] = useState('');
  const [requestedWindow, setRequestedWindow] = useState('');
  const recovery = useCommercialWriteRecovery('commercialJob.create');
  const savedRequest = recovery.read.status === 'ready' ? recovery.read.request : null;
  useEffect(() => {
    if (!savedRequest) return;
    setWho({
      customerId: savedRequest.customerId ?? '',
      propertyId: savedRequest.propertyId ?? '',
    });
    setContactName(savedRequest.contactName ?? '');
    setTitle(savedRequest.title);
    setDescription(savedRequest.description ?? '');
    setCommercialServiceTypeId(savedRequest.serviceTypeId ?? '');
    setOwner(savedRequest.ownerUserId ?? '');
    setStageId(savedRequest.stageId ?? '');
    setRequestedOn(savedRequest.requestedOn ?? '');
    setRequestedWindow(savedRequest.requestedWindow ?? '');
  }, [savedRequest]);
  const online = useOnline();
  const members = useQuery(trpc.org.members.queryOptions());
  const types = useQuery(trpc.serviceType.list.queryOptions());
  const create = useMutation(
    trpc.commercialJob.create.mutationOptions({
      onSuccess: async (result, request) => {
        if (!(await recovery.complete(request))) return;
        void qc.invalidateQueries();
        onClose();
        void navigate({ to: '/jobs/$jobId', params: { jobId: result.id } });
      },
      onError: () => {
        void qc.invalidateQueries({ queryKey: trpc.jobPipeline.pathKey() });
      },
    }),
  );
  const locked =
    create.isPending || recovery.preparing || recovery.read.status !== 'empty';
  const missing = [
    ...(!online ? ['a connection to create or recover this Job'] : []),
    ...(!who.customerId && !contactName.trim() ? ['a customer or requestor name'] : []),
    ...(!title.trim() ? ['what needs doing'] : []),
    ...(!pipeline.active ||
    !pipeline.stages.some((stage) => stage.id === stageId && stage.active)
      ? ['an active stage']
      : []),
  ];
  const send = async () => {
    if (
      !online ||
      create.isPending ||
      recovery.preparing ||
      (!savedRequest && missing.length > 0)
    )
      return;
    const request: CommercialJobCreateInput = savedRequest ?? {
      customerId: who.customerId || undefined,
      propertyId: who.propertyId || undefined,
      contactName: who.customerId ? undefined : contactName.trim(),
      title: title.trim(),
      description: description.trim() || undefined,
      pipelineId: pipeline.id,
      stageId,
      serviceTypeId: commercialServiceTypeId || undefined,
      ownerUserId: ownerUserId || undefined,
      requestedOn: requestedOn || undefined,
      requestedWindow: requestedWindow.trim() || undefined,
      ...idem(),
    };
    if (await recovery.prepare(request)) create.mutate(request);
  };
  return (
    <Sheet
      open={open}
      onClose={() => {
        if (!create.isPending && !recovery.preparing) onClose();
      }}
      title="New job"
      footer={
        <div className="space-y-3">
          {create.error ? (
            <Notice tone="danger" alert>
              {create.error.message}
            </Notice>
          ) : null}
          <CommercialWriteRecovery
            read={recovery.read}
            error={recovery.error}
            pending={create.isPending || recovery.preparing}
            online={online}
            onRetry={() => void send()}
            onResolve={() => {
              void recovery.resolveAttempt().then((cleared) => {
                if (cleared) {
                  create.reset();
                  void qc.invalidateQueries({ queryKey: trpc.jobPipeline.pathKey() });
                }
              });
            }}
          />
          {recovery.read.status === 'empty' ? (
            <CreateFooter
              label="Create job"
              reasonId={reasonId}
              missing={missing}
              pending={create.isPending || recovery.preparing}
              onSubmit={() => void send()}
            />
          ) : null}
        </div>
      }
    >
      <div className="space-y-4">
        {workspaceNotice}
        <p className="text-sm text-muted-foreground">
          A Request is recorded with this Job. Quotes and confirmed work orders stay
          linked here.
        </p>
        <fieldset disabled={locked} className="space-y-3">
          <CustomerPicker
            customerId={who.customerId}
            propertyId={who.propertyId}
            onChange={setWho}
            requireProperty={false}
          />
          {!who.customerId ? (
            <Field label="Or enter the requestor's name">
              <input
                className={field}
                value={contactName}
                maxLength={200}
                onChange={(event) => setContactName(event.target.value)}
              />
            </Field>
          ) : null}
          <Field label="What needs doing?">
            <input
              className={field}
              value={title}
              maxLength={200}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
          <Field label="Details">
            <textarea
              className={`${field} min-h-24`}
              value={description}
              maxLength={4000}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
          <Field label={`Stage · ${pipeline.label}`}>
            <select
              className={field}
              value={stageId}
              onChange={(event) => setStageId(event.target.value)}
            >
              <option value="">Choose stage…</option>
              {pipeline.stages
                .filter((stage) => stage.active)
                .map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.label}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Owner (optional)">
            <select
              className={field}
              value={ownerUserId}
              onChange={(event) => setOwner(event.target.value)}
            >
              <option value="">Unassigned</option>
              {(members.data ?? [])
                .filter((member) => can(member.role as Role, 'commercialJob', 'update'))
                .map((member) => (
                  <option key={member.userId} value={member.userId}>
                    {member.name ?? member.email}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Service type (optional)">
            <select
              className={field}
              value={commercialServiceTypeId}
              onChange={(event) => setCommercialServiceTypeId(event.target.value)}
            >
              <option value="">Not yet classified</option>
              {(types.data ?? [])
                .filter((type) => type.active)
                .map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Requested date (optional)">
            <input
              type="date"
              className={field}
              value={requestedOn}
              onChange={(event) => setRequestedOn(event.target.value)}
            />
          </Field>
          <Field label="Requested time window (optional)">
            <input
              className={field}
              value={requestedWindow}
              maxLength={200}
              onChange={(event) => setRequestedWindow(event.target.value)}
              placeholder="e.g. Morning"
            />
          </Field>
        </fieldset>
        <p className="text-xs text-muted-foreground">
          Requested dates are informational. Crew reservations begin when the office
          schedules a confirmed work order.
        </p>
      </div>
    </Sheet>
  );
}

function LegacyNewJobSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const members = useQuery(trpc.org.members.queryOptions());
  // Same predicate as the board's lanes and `visit.setAssignees` — see AddVisitSheet.
  const dispatchable = (members.data ?? []).filter((m) => canHoldVisits(m.role as Role));
  const [who, setWho] = useState({ customerId: '', propertyId: '' });
  const [title, setTitle] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  // The picker's own list (one cache entry, no second fetch): the save rule checks the picked
  // type against what the picker offers now.
  const serviceTypes = useQuery(trpc.serviceType.list.queryOptions());
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'normal' | 'urgent'>('normal');
  const [when, setWhen] = useState('');
  const [assignee, setAssignee] = useState('');
  const reasonId = useId();

  const create = useMutation(
    trpc.job.create.mutationOptions({
      onSuccess: (r) => {
        void qc.invalidateQueries();
        setWho({ customerId: '', propertyId: '' });
        setTitle('');
        setServiceTypeId('');
        setDescription('');
        setPriority('normal');
        setWhen('');
        setAssignee('');
        onClose();
        void navigate({ to: '/jobs/$jobId', params: { jobId: r.job.id } });
      },
      onError: () => refetchServiceTypes(qc, trpc),
    }),
  );

  const missing = newJobMissing(
    { ...who, title, serviceTypeId },
    offeredServiceTypes(serviceTypes.data ?? []),
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="New job"
      footer={
        <CreateFooter
          label={when ? 'Create + schedule' : 'Create job'}
          missing={missing}
          pending={create.isPending}
          reasonId={reasonId}
          onSubmit={() =>
            create.mutate({
              customerId: who.customerId,
              propertyId: who.propertyId,
              title: title.trim(),
              serviceTypeId,
              description: description.trim() || undefined,
              priority,
              visit: when
                ? {
                    scheduledStartAt: new Date(when),
                    assigneeUserIds: assignee ? [assignee] : [],
                  }
                : undefined,
              ...idem(),
            })
          }
        />
      }
    >
      <div className="space-y-3">
        {create.error ? <Notice tone="danger">{create.error.message}</Notice> : null}

        <CustomerPicker
          customerId={who.customerId}
          propertyId={who.propertyId}
          onChange={setWho}
        />

        <TemplatePicker
          appliesTo="job"
          onPick={(fill) => {
            setTitle(fill.title);
            if (fill.description) setDescription(fill.description);
          }}
        />

        <Field label="What needs doing?">
          <input
            className={field}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="No heat — furnace inspection"
          />
        </Field>

        <ServiceTypePicker value={serviceTypeId} onChange={setServiceTypeId} />

        <Field label="Details for the tech">
          <textarea
            className="min-h-[70px] w-full rounded-[var(--radius)] border border-border bg-surface p-2 text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Gate code, dog in the yard, what the customer described…"
          />
        </Field>

        <Field label="Priority">
          <select
            className={field}
            value={priority}
            onChange={(e) => setPriority(e.target.value as 'normal' | 'urgent')}
          >
            <option value="normal">Normal</option>
            <option value="urgent">Urgent</option>
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="Schedule it (optional)">
            <input
              className={field}
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </Field>
          <Field label="Assign to">
            <select
              className={field}
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              disabled={!when}
            >
              <option value="">Unassigned</option>
              {dispatchable.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name ?? m.email}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <p className="text-xs text-muted-foreground">
          No date yet? It lands in Unscheduled and shows up on the dispatch board to be
          slotted.
        </p>
      </div>
    </Sheet>
  );
}
